import 'regenerator-runtime/runtime';
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRoot } from 'react-dom/client';
import { msalConfig, apiLoginRequest, skllsMsalConfig, hasUserLoggedOut, clearLogoutFlag } from '@/authConfig.ts';
import { PublicClientApplication } from '@azure/msal-browser';
import Main from './main.tsx';
import { MsalProvider } from '@azure/msal-react';
import { axiosInstance } from './axiosInstance.ts';
import { authTelemetry } from '@/app/services/authTelemetry';
import { createAccessTokenProvider } from '@/app/services/authToken.ts';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider as MuiStylesThemeProvider } from '@mui/styles';
import { CssBaseline } from '@mui/material';
import theme from './app/styles/theme.ts';

// Force login with guest user in sklls if account is a work/school account and sklls sign in does not work properly
const useExternalInstance =
  window.location.search === '?external=true' || window.location.pathname === '/loginExternal';
const msalInstance = new PublicClientApplication(useExternalInstance ? msalConfig : skllsMsalConfig);

// Set active account on initialization
msalInstance.initialize().then(() => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    // Set the first account as active
    msalInstance.setActiveAccount(accounts[0]);
  }
  
  msalInstance.handleRedirectPromise().then((response) => {
    if (response && response.account) {
      msalInstance.setActiveAccount(response.account);
    }
  }).catch(() => {
    // Redirect handling errors are tracked via telemetry
  });
});
// create queryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const acquireAccessToken = createAccessTokenProvider({
  msalInstance,
  apiLoginRequest,
  authTelemetry,
  hasUserLoggedOut,
  clearLogoutFlag
});

axiosInstance.interceptors.request.use(async (config) => {
  const token = await acquireAccessToken();

  config.headers.Authorization = `Bearer ${token}`;

  return config;
});

const container = document.getElementById('root');
const root = createRoot(container!); // TypeScript assertion for non-null

root.render(
  <StyledEngineProvider injectFirst>
    <MuiStylesThemeProvider theme={theme}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <Main />
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </MsalProvider>
      </ThemeProvider>
    </MuiStylesThemeProvider>
  </StyledEngineProvider>
);
